/**
 * Contract with Workbench UI (React): parent chatbot listens for this postMessage type
 * when the user chooses to return from AI Workbench.
 */
export const WORKBENCH_CLOSE_POST_MESSAGE_TYPE = 'FREYA_CLOSE_AI_WORKBENCH';

/** `source` field on parent → Workbench postMessages from this shell. */
export const WORKBENCH_PARENT_MESSAGE_SOURCE = 'freya-chatbot';

/**
 * Workbench (React iframe) → parent: sent after the child has mounted and registered
 * its `message` listener so the shell can re-send the full context snapshot
 * (avoids losing the initial iframe `load` postMessage).
 */
export const WORKBENCH_READY_POST_MESSAGE_TYPE = 'FREYA_WORKBENCH_READY';

/** `source` field on Workbench → parent postMessages from the embedded app. */
export const WORKBENCH_CHILD_MESSAGE_SOURCE = 'freya-workbench';

/** Query param key Freya Fusion uses to deep-link into AI Workbench on chatbot boot. */
export const WORKBENCH_DEEP_LINK_QUERY_KEY = 'route';

/** Query param value that triggers auto-navigation to AI Workbench on chatbot boot. */
export const WORKBENCH_DEEP_LINK_ROUTE_VALUE = 'workbench';

/* ------------------------------------------------------------------ */
/* Parent (Freya chatbot shell) → Workbench iframe context envelope.  */
/* ------------------------------------------------------------------ */

/**
 * Single envelope `type` for all shell → workbench context updates.
 * Sent as either a full snapshot (iframe load / READY handshake) or a delta
 * (e.g. only `{ language }` when locale changes). The workbench listener
 * merges `context` into its local state.
 */
export const WORKBENCH_CHATBOT_CONTEXT_TYPE = 'FREYA_CHATBOT_CONTEXT';

/**
 * Schema version of `WorkbenchChatbotContext`. Bump only on **breaking**
 * changes (rename / type change / semantic change of an existing key).
 * Adding new optional keys is NOT a breaking change and does not bump
 * this — the workbench treats every key as optional.
 */
export const WORKBENCH_CHATBOT_CONTEXT_VERSION = 1 as const;

/**
 * Shell state mirrored to the workbench iframe.
 *
 * Every key is optional because we send both full snapshots (on iframe load
 * and READY handshake) and partial deltas (e.g. only `{ language }` when the
 * chatbot switches locale at runtime). Workbench-side listeners must merge
 * incoming `context` into their local state rather than replacing it.
 *
 * To add a new field:
 *  1. Add an optional key here.
 *  2. Read its current value inside `AgentMainAreaComponent.buildChatbotContextSnapshot()`.
 *  3. (If it can change at runtime) post a delta from wherever it changes.
 *
 * Existing keys:
 *  - `isRegulationsNext`: mirrors `RegulationNextService.hasRegulationNextAccess()`.
 *  - `language`: chatbot's current i18n key from `TranslateService.currentLang`
 *    (e.g. `'en'`, `'jp'`, `'zhCN'`). The workbench maps it to its
 *    `SUPPORTED_LANGUAGES` (see `@/app/i18n` in the workbench React app).
 *  - `aiAssistant`: `true` when Workbench was opened via `?route=workbench`
 *    (Freya Fusion expand / standalone); `false` when opened from the sidebar.
 */
export type WorkbenchChatbotContext = {
  isRegulationsNext?: boolean;
  language?: string;
  aiAssistant?: boolean;
};

/**
 * Wire format for parent → workbench context updates.
 *
 * Payload shape:
 * `{ type: WORKBENCH_CHATBOT_CONTEXT_TYPE, source: 'freya-chatbot',
 *    version: WORKBENCH_CHATBOT_CONTEXT_VERSION,
 *    context: Partial<WorkbenchChatbotContext> }`
 */
export type WorkbenchChatbotContextMessage = {
  type: typeof WORKBENCH_CHATBOT_CONTEXT_TYPE;
  source: typeof WORKBENCH_PARENT_MESSAGE_SOURCE;
  version: typeof WORKBENCH_CHATBOT_CONTEXT_VERSION;
  context: Partial<WorkbenchChatbotContext>;
};
